<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-text">
            <h1 class="hero-title">
              Master <span class="pyspark-highlight">PySpark</span>
              <br>with Interactive Examples
            </h1>
            <p class="hero-description">
              Comprehensive Python Spark tutorials with hands-on code samples, real-world use cases, 
              and production-ready patterns. Learn big data processing, machine learning, and streaming analytics.
            </p>
            <div class="hero-actions">
              <RouterLink to="/samples" class="btn btn-primary btn-lg">
                Explore Code Samples
              </RouterLink>
              <RouterLink to="/about" class="btn btn-outline btn-lg">
                Learn More
              </RouterLink>
            </div>
            <div class="hero-stats">
              <div class="stat">
                <span class="stat-number">20+</span>
                <span class="stat-label">Code Samples</span>
              </div>
              <div class="stat">
                <span class="stat-number">5</span>
                <span class="stat-label">Categories</span>
              </div>
              <div class="stat">
                <span class="stat-number">100%</span>
                <span class="stat-label">Hands-on</span>
              </div>
            </div>
          </div>
          <div class="hero-visual">
            <div class="code-preview">
              <div class="code-header">
                <span class="code-filename">pyspark_example.py</span>
              </div>
              <div class="code-content">
                <pre><code>from pyspark.sql import SparkSession

# Initialize Spark Session
spark = SparkSession.builder \\
    .appName("DataProcessing") \\
    .config("spark.sql.adaptive.enabled", "true") \\
    .getOrCreate()

# Read data
df = spark.read.parquet("data/sales.parquet")

# Transform data
result = df.groupBy("category") \\
    .agg({"amount": "sum", "quantity": "avg"}) \\
    .orderBy("sum(amount)", ascending=False)

# Show results
result.show()</code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="section">
      <div class="container">
        <h2 class="text-center mb-8">Why Learn PySpark Here?</h2>
        <div class="grid grid-3">
          <div class="feature-card">
            <div class="feature-icon">📊</div>
            <h3>Real-World Examples</h3>
            <p>Learn from practical code samples that solve actual data processing challenges in production environments.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">⚡</div>
            <h3>Performance Focused</h3>
            <p>Discover optimization techniques, best practices, and performance tuning strategies for large-scale data processing.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🔧</div>
            <h3>Production Ready</h3>
            <p>Code samples include error handling, monitoring, and deployment patterns used in enterprise environments.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Sample Categories -->
    <section class="section" style="background: var(--surface);">
      <div class="container">
        <h2 class="text-center mb-8">Explore Sample Categories</h2>
        <div class="grid grid-2">
          <div class="category-card">
            <div class="category-header">
              <div class="category-icon">🗂️</div>
              <h3>DataFrames & SQL</h3>
            </div>
            <p>Master DataFrame operations, SQL queries, and data manipulation techniques for structured data processing.</p>
            <ul class="category-topics">
              <li>DataFrame Basics</li>
              <li>Complex Joins</li>
              <li>Window Functions</li>
              <li>SQL Integration</li>
            </ul>
          </div>
          
          <div class="category-card">
            <div class="category-header">
              <div class="category-icon">🌊</div>
              <h3>Streaming</h3>
            </div>
            <p>Learn real-time data processing with Structured Streaming, Kafka integration, and event-driven architectures.</p>
            <ul class="category-topics">
              <li>Structured Streaming</li>
              <li>Kafka Integration</li>
              <li>Watermarks</li>
              <li>State Management</li>
            </ul>
          </div>
          
          <div class="category-card">
            <div class="category-header">
              <div class="category-icon">🤖</div>
              <h3>Machine Learning</h3>
            </div>
            <p>Build scalable ML pipelines with MLlib, feature engineering, and distributed model training and evaluation.</p>
            <ul class="category-topics">
              <li>MLlib Pipelines</li>
              <li>Feature Engineering</li>
              <li>Model Training</li>
              <li>Evaluation Metrics</li>
            </ul>
          </div>
          
          <div class="category-card">
            <div class="category-header">
              <div class="category-icon">⚡</div>
              <h3>Performance</h3>
            </div>
            <p>Optimize Spark applications with caching strategies, partitioning, and resource management techniques.</p>
            <ul class="category-topics">
              <li>Caching Strategies</li>
              <li>Partitioning</li>
              <li>Resource Tuning</li>
              <li>Memory Management</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section">
      <div class="container">
        <div class="cta-card">
          <div class="cta-content">
            <h2>Ready to Start Learning?</h2>
            <p>Dive into our comprehensive collection of PySpark code samples and tutorials.</p>
            <div class="cta-actions">
              <RouterLink to="/samples" class="btn btn-primary btn-lg">
                Browse All Samples
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
</script>

<style scoped>
.hero {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-color-dark));
  color: white;
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
}

.hero-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
}

.hero-description {
  font-size: 1.25rem;
  line-height: 1.6;
  margin-bottom: 2.5rem;
  opacity: 0.95;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
}

.hero-stats {
  display: flex;
  gap: 2rem;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: white;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.8;
}

.hero-visual {
  position: relative;
}

.code-preview {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: var(--radius-xl);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-xl);
}

.code-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.code-filename {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.code-content {
  padding: 1.5rem;
}

.code-content pre {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  line-height: 1.6;
  overflow-x: auto;
}

.feature-card {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
  text-align: center;
  transition: var(--transition);
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 1.5rem;
}

.feature-card h3 {
  color: var(--text-color);
  margin-bottom: 1rem;
}

.feature-card p {
  color: var(--text-light);
  line-height: 1.6;
}

.category-card {
  background: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
  transition: var(--transition);
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.category-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.category-icon {
  font-size: 2rem;
}

.category-card h3 {
  color: var(--text-color);
  margin: 0;
}

.category-card p {
  color: var(--text-light);
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.category-topics {
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
}

.category-topics li {
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.category-topics li::before {
  content: "▸";
  margin-right: 0.5rem;
  color: var(--primary-color);
}

.cta-card {
  background: linear-gradient(135deg, var(--surface), #f1f5f9);
  padding: 4rem;
  border-radius: var(--radius-xl);
  text-align: center;
  border: 2px solid var(--primary-color);
}

.cta-content h2 {
  color: var(--text-color);
  margin-bottom: 1rem;
}

.cta-content p {
  color: var(--text-light);
  font-size: 1.125rem;
  margin-bottom: 2rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .hero-content {
    gap: 3rem;
  }
  
  .hero-title {
    font-size: 3rem;
  }
  
  .hero-actions {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .hero-stats {
    justify-content: space-around;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 4rem 0;
  }
  
  .hero-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-description {
    font-size: 1.125rem;
  }
  
  .hero-actions {
    align-items: center;
  }
  
  .code-content pre {
    font-size: 0.8rem;
  }
  
  .category-topics {
    grid-template-columns: 1fr;
  }
  
  .cta-card {
    padding: 2rem;
  }
}
</style>